import React from 'react';
import { TruncatedCopy } from 'common/Text/TruncatedCopy';
import styled from 'styled-components';
import { TerminationReasonModel } from '@utmost.co/enterprise-models';
import { DataTableTypes } from '@utmost.co/utmost-design-system';
import { useDeepMemo, StatusChipType, StatusChip } from '@beeline.com/bp-design-system';
import { generateTypeName } from 'src/utils/TypeNames';
import { TerminationReasonActions } from './TerminationReasonActions';
import { useTerminationReasonsIntl } from './translations/useTerminationReasonsIntl';

interface TerminationReasonTypeColumnsParam {
  onEdit: (reason: TerminationReasonModel) => void;
  onDelete: (uid: string) => void;
  terminationReasons?: TerminationReasonModel[];
}

export type TerminationReasonTypeColumn = TerminationReasonModel & { '[menu]': string };
type RateTypeColumns = DataTableTypes.Column<TerminationReasonTypeColumn>[];

export const useTerminationReasonsColumns = ({
  onDelete,
  onEdit,
  terminationReasons
}: TerminationReasonTypeColumnsParam): RateTypeColumns => {
  const intl = useTerminationReasonsIntl();

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  return useDeepMemo(
    (): DataTableTypes.Column<TerminationReasonTypeColumn & { uid: string }>[] => [
      {
        Header: intl.name,
        accessor: 'name',
        defaultWidth: 300,
        Cell: ({ value }) => <span data-test="reason-name">{value ?? '-'}</span>
      },
      {
        Header: intl.referenceId,
        accessor: 'referenceId',
        ellipsisOnOverflow: false,
        Cell: ({ value }) => (
          <StyledReferenceId data-test="reason-ref-id">
            <TruncatedCopy textCopy={value ?? '-'} />
          </StyledReferenceId>
        )
      },
      {
        Header: intl.status,
        accessor: 'status',
        minWidth: 120,
        Cell: ({ value }) => (
          <span data-test="reason-status">
            <span data-test={value === 'ACTIVE' ? 'active' : 'inactive'}>
              <StatusChip
                label={generateTypeName(value)}
                type={value === 'ACTIVE' ? StatusChipType.LIGHT_BLUE : StatusChipType.GREY}
              />
            </span>
          </span>
        )
      },
      {
        Header: intl.assignable,
        minWidth: 120,
        accessor: 'assignable',
        Cell: ({ value }) => <span data-test="reason-assignable">{value ? 'Enabled' : 'Disabled'}</span>
      },
      {
        Header: intl.eligibleForRehire,
        minWidth: 120,
        accessor: 'eligibleForRehire',
        Cell: ({ value }) => <span data-test="reason-eligible-for-rehire">{value ? 'Yes' : 'No'}</span>
      },
      {
        Header: '',
        accessor: '[menu]',
        sticky: 'right',
        Cell: ({ row }) => (
          <TerminationReasonActions onDelete={onDelete} onEdit={onEdit} terminationReason={row.original} />
        )
      }
    ],
    // Adding terminationReasons as a dependency to prevent stale
    // data after termination reasons have been updated via the UI.
    [onEdit, onDelete, terminationReasons]
  );
};

const StyledReferenceId = styled.span`
  width: 100%;
`;
